<script setup>
import { computed, ref } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { useAlert } from 'dashboard/composables';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import {
  dynamicTimeLocalized,
  dateFormatLocalized,
} from 'shared/helpers/timeHelper';
import { useContactDetailI18n } from '../../useContactDetailI18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Editor from 'dashboard/components/widgets/WootWriter/Editor.vue';

const props = defineProps({
  note: { type: Object, required: true },
  writtenBy: { type: String, required: true },
  canEdit: { type: Boolean, default: false },
  onUpdate: { type: Function, required: true },
});
const emit = defineEmits(['delete']);
const { t } = useContactDetailI18n();
const { formatMessage } = useMessageFormatter();
const hasText = computed(
  () =>
    new DOMParser()
      .parseFromString(formatMessage(draft.value || ''), 'text/html')
      .body.textContent.trim().length > 0
);
const showMenu = ref(false);
const editing = ref(false);
const confirmingDelete = ref(false);
const draft = ref('');
const saving = ref(false);
const edited = computed(() => props.note.updatedAt > props.note.createdAt);
const fullDate = computed(() =>
  dateFormatLocalized(props.note.createdAt, "dd/MM/yyyy 'às' HH:mm")
);
const startEdit = () => {
  draft.value = props.note.content;
  editing.value = true;
  showMenu.value = false;
};
const save = async () => {
  if (!hasText.value || saving.value) return;
  saving.value = true;
  try {
    await props.onUpdate({ noteId: props.note.id, content: draft.value });
    editing.value = false;
  } catch {
    useAlert(t('CONTACT_DETAIL.NOTE_ERROR'));
  } finally {
    saving.value = false;
  }
};
const onKeydown = event => {
  if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
    event.preventDefault();
    event.stopPropagation();
    save();
  }
};
</script>

<template>
  <div
    data-note-row
    class="grid grid-cols-[36px_minmax(0,1fr)_36px] items-start gap-3 border-b border-[#F0F1F3] py-4 last:border-b-0"
  >
    <Avatar
      :name="note.user?.name || 'Bot'"
      :src="note.user?.thumbnail || ''"
      :size="32"
      rounded-full
      class="[&>span[role=img]]:!bg-[#FDEBD8] [&_.select-none]:!text-[11px] [&>span[role=img]]:!font-bold [&>span[role=img]]:!text-[#A34D00]"
    />
    <div class="flex min-w-0 flex-col gap-1">
      <div class="flex flex-wrap items-center gap-1 text-[13px] leading-5">
        <span class="font-bold text-[#101828]">{{ writtenBy }}</span>
        <span class="text-[#8C959F]">·</span>
        <time
          :title="fullDate"
          :datetime="new Date(note.createdAt * 1000).toISOString()"
          class="text-[#8C959F]"
          >{{ dynamicTimeLocalized(note.createdAt) }}</time
        >
        <span v-if="edited" class="text-xs text-[#8C959F]">{{
          t('CONTACT_DETAIL.EDITED')
        }}</span>
      </div>
      <div v-if="editing" @keydown.capture="onKeydown">
        <Editor
          :model-value="draft"
          :disabled="saving"
          :enable-canned-responses="false"
          :override-line-breaks="true"
          :enabled-menu-options="['strong', 'em', 'strike', 'bulletList']"
          class="rounded-lg border border-[#F0F1F3] [&_.ProseMirror-menubar]:!static [&_.ProseMirror-menubar]:!border-b [&_.ProseMirror-menubar]:!border-[#F0F1F3] [&_.ProseMirror-menubar]:!bg-[#FAFAFA] [&_.ProseMirror]:!min-h-[96px] [&_.ProseMirror]:!px-4 [&_.ProseMirror]:!py-3"
          @input="draft = $event"
        />
        <div class="mt-3 flex justify-end gap-2">
          <button
            type="button"
            :disabled="saving"
            class="rounded-lg px-3 py-2 text-[13px] text-[#101828] hover:bg-[#F5F5F3]"
            @click="editing = false"
          >
            {{ t('CONTACT_DETAIL.CANCEL') }}
          </button>
          <button
            type="button"
            :disabled="saving || !hasText"
            class="rounded-lg bg-[#16A34A] px-3 py-2 text-[13px] text-white disabled:bg-[#BBE5C8]"
            @click="save"
          >
            {{ t('CONTACT_DETAIL.SAVE') }}
          </button>
        </div>
      </div>
      <div
        v-else
        v-dompurify-html="formatMessage(note.content || '')"
        class="prose-sm break-words text-left text-[14px] leading-[1.5] text-[#101828] [&_p]:m-0 [&_p]:text-[14px] [&_p]:leading-[1.5] [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5"
      />
      <div
        v-if="confirmingDelete"
        role="alertdialog"
        :aria-label="t('CONTACT_DETAIL.DELETE_CONFIRM')"
        class="mt-2 rounded-lg bg-[#FAFAFA] p-3"
      >
        <p class="m-0 text-[13px] text-[#101828]">
          {{ t('CONTACT_DETAIL.DELETE_CONFIRM') }}
        </p>
        <div class="mt-2 flex justify-end gap-2">
          <button
            type="button"
            class="rounded-lg px-3 py-2 text-[13px] hover:bg-[#F5F5F3]"
            @click="confirmingDelete = false"
          >
            {{ t('CONTACT_DETAIL.CANCEL') }}
          </button>
          <button
            type="button"
            class="rounded-lg bg-[#B42318] px-3 py-2 text-[13px] text-white"
            @click="
              emit('delete', note.id);
              confirmingDelete = false;
            "
          >
            {{ t('CONTACT_DETAIL.DELETE') }}
          </button>
        </div>
      </div>
    </div>
    <div class="relative">
      <div
        v-if="canEdit && !editing"
        v-on-click-outside="() => (showMenu = false)"
        @keydown.esc="showMenu = false"
      >
        <button
          type="button"
          :aria-label="t('CONTACT_DETAIL_VIEW.MORE')"
          :aria-expanded="showMenu"
          aria-haspopup="menu"
          class="flex size-8 items-center justify-center rounded-lg text-[#8C959F] hover:bg-[#F5F5F3]"
          @click="showMenu = !showMenu"
        >
          <span class="i-ph-dots-three size-4" />
        </button>
        <div
          v-if="showMenu"
          role="menu"
          class="absolute right-0 top-9 z-20 w-[180px] rounded-xl bg-white p-1 shadow-[0_4px_16px_rgba(16,24,40,0.15)]"
        >
          <button
            type="button"
            role="menuitem"
            class="w-full rounded-lg px-3 py-2 text-left text-[13px] text-[#101828] hover:bg-[#F5F5F3]"
            @click="startEdit"
          >
            {{ t('CONTACT_DETAIL.EDIT_NOTE') }}
          </button>
          <button
            type="button"
            role="menuitem"
            class="w-full rounded-lg px-3 py-2 text-left text-[13px] text-[#B42318] hover:bg-[#F5F5F3]"
            @click="
              confirmingDelete = true;
              showMenu = false;
            "
          >
            {{ t('CONTACT_DETAIL.DELETE') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
